import {
    GRID_COLUMNS,
    MOBILE_GRID_COLUMNS,
    STUDIO_DESKTOP_CANVAS_WIDTH,
    STUDIO_MOBILE_CANVAS_WIDTH,
} from 'Vendor/Utils/Constants'
import {
    GridItemPosition,
    GridItemSize,
} from 'Vendor/Utils/StudioTypes'
import { LayoutItem, PageData } from '../initialState'
import React from 'react'

const DESKTOP_TO_MOBILE_SCALE =
    STUDIO_MOBILE_CANVAS_WIDTH / STUDIO_DESKTOP_CANVAS_WIDTH

const SCALE_STYLE_PROPS = new Set<keyof React.CSSProperties>([
    'fontSize',
    'outlineWidth',
    'letterSpacing',
    'lineHeight',
    'borderWidth',
    'borderRadius',
    'padding',
    'paddingTop',
    'paddingRight',
    'paddingBottom',
    'paddingLeft',
    'margin',
    'marginTop',
    'marginRight',
    'marginBottom',
    'marginLeft',
    'gap',
    'columnGap',
    'rowGap',
    'width',
    'height',
    'maxWidth',
    'minWidth',
    'maxHeight',
    'minHeight',
])

const scaleByResponsiveRatio = (value: number) => {
    const ratio = MOBILE_GRID_COLUMNS / GRID_COLUMNS
    return Math.max(1, Math.round(value * ratio))
}

const formatScaledNumber = (value: number) => {
    return Number(value.toFixed(2)).toString()
}

const scaleCssNumericToken = (token: string, scale = DESKTOP_TO_MOBILE_SCALE) => {
    return token.replace(/(-?\d*\.?\d+)(px|rem|em|pt)/g, (_match, raw, unit) => {
        const n = Number(raw)
        if (Number.isNaN(n)) return `${raw}${unit}`
        return `${formatScaledNumber(n * scale)}${unit}`
    })
}

const scaleStyleValue = (
    value: React.CSSProperties[keyof React.CSSProperties],
): React.CSSProperties[keyof React.CSSProperties] => {
    if (typeof value === 'number') {
        return Number(formatScaledNumber(value * DESKTOP_TO_MOBILE_SCALE))
    }

    if (typeof value === 'string') {
        // Scale values like "16px", "1.2rem", "8px 12px" while preserving other tokens.
        return scaleCssNumericToken(value, DESKTOP_TO_MOBILE_SCALE)
    }

    return value
}

export const toMobileStyles = (
    styles: React.CSSProperties = {},
): React.CSSProperties => {
    const scaledStyles: Record<string, unknown> = { ...styles }

    Object.entries(scaledStyles).forEach(([key, value]) => {
        const typedKey = key as keyof React.CSSProperties
        if (!SCALE_STYLE_PROPS.has(typedKey)) return
        scaledStyles[key] = scaleStyleValue(
            value as React.CSSProperties[keyof React.CSSProperties],
        )
    })

    return scaledStyles as React.CSSProperties
}

export const toMobileSize = (size: GridItemSize): GridItemSize => ({
    col_span: Math.min(MOBILE_GRID_COLUMNS, scaleByResponsiveRatio(size.col_span)),
    row_span: scaleByResponsiveRatio(size.row_span),
})

export const toMobilePosition = (
    position: GridItemPosition,
    colSpan: number,
): GridItemPosition => ({
    col_start: Math.max(
        1,
        Math.min(
            MOBILE_GRID_COLUMNS - Math.min(MOBILE_GRID_COLUMNS, colSpan) + 1,
            position.col_start,
        ),
    ),
    row_start: Math.max(1, position.row_start),
})

export const createMobileLayoutItemFromDesktop = (
    desktopItem: LayoutItem,
): LayoutItem => {
    const mobileSize = toMobileSize(desktopItem.size)
    return {
        ...desktopItem,
        position: toMobilePosition(desktopItem.position, mobileSize.col_span),
        size: mobileSize,
        styles: toMobileStyles(desktopItem.styles),
    }
}

export const syncPageMobileFromDesktop = (page: PageData) => {
    const desktop = page.layouts.desktop
    const mobile = page.layouts.mobile

    const rebuiltMobileItems: Record<string, LayoutItem> = {}

    desktop.itemIds.forEach((itemId) => {
        const desktopItem = desktop.items[itemId]
        if (!desktopItem) return

        rebuiltMobileItems[itemId] = createMobileLayoutItemFromDesktop(desktopItem)
    })

    mobile.items = rebuiltMobileItems
    mobile.itemIds = [...desktop.itemIds]
}
